import { Box, Collapse, Grid } from '@mui/material';
import CustomSnackbar from 'components/CustomSnackbar';
import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter.js';
// import LiveClock from 'components/LiveClock/LiveClock.js';
import MDTypography from 'components/MDTypography/index';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// import { SensorCard } from './SensorCard';
import { fetchUserDevices, setCurrentPage } from 'stores/sensor/sensorStore';
import { filterDevices } from '../../helpers/filterHelpers';
// import { fetchUserBuildingsDetails } from '../../stores/sensor/sensorStore';
import { SimpleControllerCard } from '../Controllers/SimpleControllerCard';
import { useNavigate } from 'react-router-dom';
import { fetchControllers } from '../../stores/sensor/controllersSlice';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const REFRESH_TIME_IN_SECONDS = 300;

export const ManageControllersList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector(state => state.sensors.currentUser);
  // const devices = useSelector(state => state.sensors.devices);
  const { selectedFilterObject } = useSelector(state => state.persistence);
  const controllers = useSelector(state => state.controllers.controllers);

  const { distributor, customer, building, floor, area, status, searchString } = selectedFilterObject;

  const [snackbarState, setSnackbarState] = useState({
    snackbarOpen: false,
    vertical: 'top',
    horizontal: 'center',
  });
  const [unassignedOpen, setUnassignedOpen] = useState(true);
  const [assignedOpen, setAssignedOpen] = useState(true);
  const { vertical, horizontal, snackbarOpen } = snackbarState;

  // UNASSIGNED devices: Never filtered, always show all
  const unassignedDevices = useMemo(() => {
    const unassigned = controllers.filter(device => device.building_id === null || device.building_id === '');
    if (!searchString) return unassigned;
    const lower = searchString.toLowerCase();
    return unassigned.filter(
      device => device.name?.toLowerCase().includes(lower) || device.id?.toString().includes(searchString),
    );
  }, [controllers, searchString]);

  // ASSIGNED devices: Apply all filters
  const assignedDevices = useMemo(() => {
    // First get only assigned devices
    const assigned = controllers.filter(device => device.building_id !== null && device.building_id !== '');

    // Then apply filtering to assigned devices only
    const filtered = filterDevices(
      assigned,
      {
        distributor,
        customer,
        building,
        floor,
        area,
        status,
        searchString,
      },
      currentUser,
    );

    // Apply search string filtering
    // const searchFiltered = searchString
    //   ? filtered?.filter(
    //       device =>
    //         device.name?.toLowerCase().includes(searchString.toLowerCase()) ||
    //         device.id?.toString().includes(searchString),
    //     )
    //   : filtered;

    // Sort by status (Green → Yellow → Red → Grey)
    return filtered.sort((a, b) => {
      const statusOrder = { 1: 1, 2: 2, 3: 3, 4: 4 };
      const priorityA = statusOrder[a.status] || 4;
      const priorityB = statusOrder[b.status] || 4;

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      return (a.name || '').localeCompare(b.name || '');
    });
  }, [controllers, distributor, customer, building, floor, area, status, searchString]);
  // Set current page for navigation tracking (safe, no dependencies)
  useEffect(() => {
    dispatch(setCurrentPage('manage-controllers'));
  }, [dispatch]);

  // Initialize data on mount (stable)
  useEffect(() => {
    const initializeData = async () => {
      // dispatch(fetchUserDevices({ devicesType: 'Sensor' }));
      dispatch(fetchControllers());
      // dispatch(fetchUserBuildingsDetails());
    };

    if (controllers.length === 0) {
      initializeData();
    }
  }, [dispatch]);

  // Auto-refresh sensors data (stable interval)
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(fetchUserDevices({ devicesType: 'Controller' }));
    }, REFRESH_TIME_IN_SECONDS * 1000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Helper functions
  const handleSnackbarClose = () => {
    setSnackbarState({ ...snackbarState, snackbarOpen: false });
  };

  const getDisplayTitle = () => {
    return `Manage Controllers`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* ===== HEADER WITH COUNTS ===== */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <MDTypography variant="h4" sx={{ ml: 0.2, color: 'black' }}>
          {getDisplayTitle()}
        </MDTypography>

        <CustomSnackbar
          open={snackbarState.snackbarOpen}
          duration={3000}
          onClose={handleSnackbarClose}
          message="Please select a distributor and a customer"
          vertical={vertical}
          horizontal={horizontal}
        />
      </Box>

      {/* ===== MAIN CONTENT ===== */}
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Box ml={1} mb={1} sx={{ width: '100%' }}>
          {/* ===== FILTER COMPONENT ===== */}
          <DevicesListFilter type="controllers" status={false} />

          {/* ===== UNASSIGNED DEVICES ===== */}
          {currentUser.role !== 'c' && (
            <Box sx={{ mb: 3, mt: 3 }}>
              <Box
                onClick={() => setUnassignedOpen(prev => !prev)}
                sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', mb: 2 }}>
                <MDTypography variant="h5" sx={{ color: '#BE1E2D' }}>
                  UnAssigned Devices ({unassignedDevices.filter(c => c.device_type?.startsWith('Controller')).length})
                </MDTypography>
                {unassignedOpen ? (
                  <ExpandLessIcon sx={{ color: '#BE1E2D', ml: 0.5 }} />
                ) : (
                  <ExpandMoreIcon sx={{ color: '#BE1E2D', ml: 0.5 }} />
                )}
              </Box>
              <Collapse in={unassignedOpen} timeout={250} unmountOnExit>
                <Grid
                  className="devicesListContainer"
                  sx={{
                    display: 'flex',
                    alignItems: 'start',
                    height: 'fit-content',
                    maxHeight: '71vh',
                    overflow: 'auto',
                    width: '88vw',
                    flexWrap: 'wrap',
                    overflowX: 'hidden',
                  }}
                  margin={0}>
                  {unassignedDevices
                    .filter(c => c.device_type?.startsWith('Controller'))
                    .map(c => (
                      <Grid
                        container
                        sx={{
                          lineHeight: 0,
                          position: 'relative',
                          marginBottom: '1rem',
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: 'center',
                          display: 'flex',
                          justifyContent: { xs: 'center', sm: 'start' },
                        }}
                        xs={12}
                        sm={12}
                        md={6}
                        lg={4}
                        xl={3}
                        key={c.id}>
                        <SimpleControllerCard
                          id={c.id}
                          name={c.name}
                          building_name={c.building_name}
                          floorNumber={c.floor_number}
                          area_name={c.area_name}
                          deviceType="controller"
                          parentComponent="ManageControllersList"
                          hoverAnimation={true}
                        />
                      </Grid>
                    ))}
                </Grid>
              </Collapse>
            </Box>
          )}

          {/* ===== ASSIGNED DEVICES ===== */}
          <Box sx={{ mb: 3 }}>
            {currentUser.role !== 'c' && (
              <Box
                onClick={() => setAssignedOpen(prev => !prev)}
                sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', mb: 2 }}>
                <MDTypography variant="h5" sx={{ color: '#00A551' }}>
                  Assigned Devices ({assignedDevices.filter(d => d.device_type?.startsWith('Controller')).length})
                </MDTypography>
                {assignedOpen ? (
                  <ExpandLessIcon sx={{ color: '#00A551', ml: 0.5 }} />
                ) : (
                  <ExpandMoreIcon sx={{ color: '#00A551', ml: 0.5 }} />
                )}
              </Box>
            )}
            <Collapse in={assignedOpen || currentUser.role === 'c'} timeout={250} unmountOnExit>
              <Grid
                className="devicesListContainer"
                sx={{
                  display: 'flex',
                  alignItems: 'start',
                  height: 'fit-content',
                  maxHeight: '71vh',
                  overflow: 'auto',
                  width: '88vw',
                  flexWrap: 'wrap',
                  overflow: 'visible',
                }}
                margin={0}>
                {assignedDevices
                  .filter(c => c.device_type?.startsWith('Controller'))
                  .map(c => (
                    <Grid
                      container
                      sx={{
                        lineHeight: 0,
                        position: 'relative',
                        marginBottom: '1rem',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: { xs: 'center', sm: 'start' },
                      }}
                      xs={12}
                      sm={12}
                      md={6}
                      lg={4}
                      xl={3}
                      key={c.id}>
                      <SimpleControllerCard
                        id={c.id}
                        name={c.name}
                        building_name={c.building_name}
                        floorNumber={c.floor_number}
                        area_name={c.area_name}
                        deviceType="controller"
                        parentComponent="ManageControllersList"
                        hoverAnimation={true}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Collapse>
          </Box>

          {/* ===== EMPTY STATE ===== */}
          {unassignedDevices.length === 0 && assignedDevices.length === 0 && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <MDTypography variant="h6" color="text">
                No devices found
              </MDTypography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ManageControllersList;
